import { initializeApp, getApps, getApp } from "firebase/app";
import { 
  getAnalytics, 
  isSupported as isAnalyticsSupported 
} from "firebase/analytics";
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';
import { 
  getAuth, 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  signInWithPopup
} from "firebase/auth";
import { 
  initializeFirestore,
  getFirestore,
  collection,
  increment,
  persistentLocalCache,
  persistentSingleTabManager,
  connectFirestoreEmulator
} from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getFunctions } from 'firebase/functions';

// Use environment variables for configuration
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || '',
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || '',
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || '',
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || '',
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '',
  appId: process.env.REACT_APP_FIREBASE_APP_ID || '',
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || ''
};

// Validate Firebase configuration
const validateConfig = (config) => {
  const requiredKeys = ['apiKey', 'authDomain', 'projectId'];
  const missingKeys = requiredKeys.filter(key => !config[key]);
  
  if (missingKeys.length > 0) {
    console.error('Missing Firebase configuration keys:', missingKeys);
    throw new Error('Invalid Firebase configuration');
  }
};

// Validate configuration before initializing
validateConfig(firebaseConfig);

// Initialize Firebase app once (resilient to HMR)
const app = getApps().length ? getApp() : initializeApp(firebaseConfig);

// Initialize services
const auth = getAuth(app);

// Initialize Firestore with persistence only once (resilient to HMR)
// Allow disabling persistence via REACT_APP_DISABLE_FIRESTORE_PERSISTENCE=true
let db;
try {
  const disablePersistence = process.env.REACT_APP_DISABLE_FIRESTORE_PERSISTENCE === 'true';

  if (!disablePersistence && typeof window !== 'undefined' && window.__firestore_db__) {
    db = window.__firestore_db__;
  } else if (!disablePersistence) {
    db = initializeFirestore(app, {
      localCache: persistentLocalCache({
        tabManager: persistentSingleTabManager()
      }),
      experimentalAutoDetectLongPolling: true
    });
    if (typeof window !== 'undefined') {
      window.__firestore_db__ = db;
    }
  } else {
    // Fallback: no persistent local cache (avoids some IndexedDB-related assertion bugs)
    db = getFirestore(app);
  }
} catch (e) {
  // If initializeFirestore throws because Firestore was already initialized by another path, fall back to getFirestore
  db = getFirestore(app);
}

// Optional: use Firestore Emulator for local testing (no real quota usage)
// Set REACT_APP_USE_FIRESTORE_EMULATOR=true and optionally REACT_APP_FIRESTORE_EMULATOR_HOST, REACT_APP_FIRESTORE_EMULATOR_PORT
try {
  if (process.env.REACT_APP_USE_FIRESTORE_EMULATOR === 'true' && !window.__firestore_emulator_connected__) {
    const host = process.env.REACT_APP_FIRESTORE_EMULATOR_HOST || 'localhost';
    const port = Number(process.env.REACT_APP_FIRESTORE_EMULATOR_PORT || 8080);
    // connectFirestoreEmulator must be called after Firestore init and before any reads/writes
    connectFirestoreEmulator(db, host, port);
    console.info(`Connected Firestore to emulator at ${host}:${port}`);
    window.__firestore_emulator_connected__ = true;
  }
} catch (e) {
  console.warn('Firestore emulator connection skipped:', e?.message || e);
}
const storage = getStorage(app);
const functions = getFunctions(app);
const googleProvider = new GoogleAuthProvider();

// Add collections references
const usersRef = collection(db, 'users');
const trendingSearchesRef = collection(db, 'trending_searches');
const issuesRef = collection(db, 'issues');
const locationsRef = collection(db, 'locations');

// Conditionally initialize analytics
let analytics;
if (typeof window !== 'undefined') {
  isAnalyticsSupported().then((supported) => {
    if (supported) {
      analytics = getAnalytics(app);
    }
  });
}

// Initialize App Check (optional, but required if your project enforces it)
// - Provide REACT_APP_RECAPTCHA_V3_SITE_KEY to enable
// - For local dev, set REACT_APP_APPCHECK_DEBUG_TOKEN to a token or 'true'
if (typeof window !== 'undefined') {
  try {
    const debugToken = process.env.REACT_APP_APPCHECK_DEBUG_TOKEN;
    if (debugToken) {
      // "true" will auto-generate a debug token and log it in the console.
      // Otherwise, provide an explicit token value.
      window.FIREBASE_APPCHECK_DEBUG_TOKEN = debugToken === 'true' ? true : debugToken;
    }
    const siteKey = process.env.REACT_APP_RECAPTCHA_V3_SITE_KEY;
    if (siteKey || debugToken) {
      // When debugToken is set, the provider won't actually perform a reCAPTCHA challenge
      const provider = new ReCaptchaV3Provider(siteKey || 'debug-site-key');
      initializeAppCheck(app, {
        provider,
        isTokenAutoRefreshEnabled: true,
      });
    }
  } catch (e) {
    // Do not fail app startup if App Check init has issues
    console.warn('App Check initialization skipped:', e?.message || e);
  }
}

export { 
  auth, 
  db,
  usersRef,
  trendingSearchesRef,
  issuesRef,
  locationsRef,
  googleProvider,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithPopup,
  increment,
  storage,
  analytics,
  functions
};